import { useEffect } from "react";
import "@/App.css";
import SiteHeader from "@/components/SiteHeader";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Mail, Phone, MapPin, Clock, CreditCard, ShieldCheck, CalendarX2, Building2 } from "lucide-react";

const JANE_URL = "https://hayahealingpsychotherapy.janeapp.com/";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: 0.05 + i * 0.08, ease } }),
};

const Reveal = ({ i = 0, className = "", children, ...rest }) => (
  <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="visible"
    viewport={{ once: true, margin: "-60px" }} className={className} {...rest}>
    {children}
  </motion.div>
);

const SECTIONS = [
  {
    icon: Clock,
    title: "Session Length & Fees",
    body: [
      "Individual sessions are 50 minutes. Initial intake sessions are 90 minutes, allowing time for a thorough assessment and collaborative treatment planning.",
      "Couples and family sessions are typically 60 to 75 minutes. Current session fees are shared during your complimentary 15-minute consultation and confirmed before your first appointment — no surprises.",
    ],
  },
  {
    icon: CalendarX2,
    title: "Cancellation Policy",
    body: [
      "We require 48 hours' notice to cancel or change an appointment. Appointments cancelled or missed with less than 48 hours' notice are charged the full session fee.",
      "This policy protects time reserved specifically for you and allows us to offer the space to another client waiting for care.",
    ],
  },
  {
    icon: CreditCard,
    title: "Payment Methods",
    body: [
      "Payment is collected at the time of your session through the secure Jane App booking platform. Details on accepted payment methods are provided when you book.",
    ],
  },
  {
    icon: Building2,
    title: "Insurance & Extended Health Benefits",
    body: [
      "We do not direct bill insurance providers. After each session, you receive a detailed receipt to submit to your extended health benefits plan for reimbursement.",
      "Many plans cover services provided by a Registered Psychotherapist (Qualifying) — please confirm your specific coverage directly with your provider before your first session.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Coverage for Registered Psychotherapists",
    body: [
      "Services at Haya Healing Psychotherapy are provided by a Registered Psychotherapist (Qualifying) registered with the College of Registered Psychotherapists of Ontario (CRPO).",
      "Coverage varies between plans: some insurers list 'Psychotherapist' or 'Registered Psychotherapist' explicitly, while others cover services under supervised practice. When checking your benefits, ask specifically about coverage for a Registered Psychotherapist (Qualifying) under CRPO. We're happy to provide any documentation your insurer requires.",
    ],
  },
];

export default function Fees() {
  useEffect(() => {
    document.title = "Fees & Insurance | Therapy Costs in Milton & Burlington | Haya Healing Psychotherapy";
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", "Session fees, cancellation policy, payment methods, and insurance coverage for psychotherapy with a Registered Psychotherapist (Qualifying) in Milton & Burlington and online across Ontario.");
  }, []);
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <section className="px-6 md:px-14 pt-14 md:pt-20 pb-12 max-w-4xl">
        <motion.a variants={fadeUp} initial="hidden" animate="visible" custom={0} href="/" className="inline-flex items-center gap-2 text-sm text-[#2c1f20]/60 hover:text-[#6e4550] transition-colors">
          <ArrowLeft size={14} /> Back home
        </motion.a>
        <motion.p variants={fadeUp} initial="hidden" animate="visible" custom={1} className="eyebrow mt-8">Practical details · Milton, Burlington &amp; Online Across Ontario</motion.p>
        <motion.h1 variants={fadeUp} initial="hidden" animate="visible" custom={2} data-testid="fees-heading"
          className="font-serif-display mt-4 text-[clamp(2.2rem,5vw,4rem)] leading-[1.08] font-normal">
          Fees &amp; <em className="text-[#6e4550]">Insurance.</em>
        </motion.h1>
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-6 space-y-4 text-[#2c1f20]/75 leading-relaxed max-w-2xl">
          <p>
            A clinical practice should make the practical side of care easy to find. Everything about session
            length, fees, cancellation, payment, and insurance coverage is gathered here — and anything unclear
            can be answered in a complimentary 15-minute consultation.
          </p>
        </motion.div>
      </section>
      <section data-testid="fees-sections" className="px-6 md:px-14 py-16 md:py-20 border-t border-[#2c1f20]/10">
        <div className="grid md:grid-cols-2 gap-6 max-w-6xl">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} i={i % 4} className="card" >
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-[#f4e6e2] flex items-center justify-center text-[#8a5a63]"><s.icon size={18} /></span>
                <h2 className="font-serif-display text-2xl leading-snug">{s.title}</h2>
              </div>
              <div className="mt-4 space-y-3">
                {s.body.map((p) => <p key={p} className="text-sm text-[#2c1f20]/70 leading-relaxed">{p}</p>)}
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="px-6 md:px-14 py-10 border-t border-[#2c1f20]/10">
        <p className="text-xs text-[#2c1f20]/50 leading-relaxed max-w-5xl">
          Fees and insurance information for Haya Healing Psychotherapy — psychotherapy and counselling in Milton, Burlington, Oakville, Mississauga, Brampton, Halton Region, and the Greater Toronto Area, with virtual sessions across Ontario. Care led by Momna Sajid, Registered Psychotherapist (Qualifying), CRPO. Sessions in English and Urdu.
        </p>
      </section>
      <footer className="px-6 md:px-14 py-16 md:py-24 bg-[#f1ece4]/50">
        <Reveal><p className="eyebrow">Begin here</p></Reveal>
        <Reveal i={1}><h2 className="font-serif-display text-4xl md:text-5xl mt-4 max-w-2xl leading-tight">Book a <em className="text-[#6e4550]">complimentary initial consultation.</em></h2></Reveal>
        <Reveal i={2} className="mt-8 flex flex-wrap gap-6 items-center">
          <a data-testid="fees-footer-book" href={JANE_URL} target="_blank" rel="noreferrer" className="pill-btn pill-solid">Book via Jane App <ArrowUpRight size={15} /></a>
          <a href="mailto:momna@hayahealingpsychotherapy.com" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Mail size={15} /> momna@hayahealingpsychotherapy.com</a>
          <a href="tel:+13656172135" className="flex items-center gap-2 text-sm text-[#2c1f20]/75 hover:text-[#6e4550] transition-colors"><Phone size={15} /> (365) 617-2135</a>
        </Reveal>
        <p className="mt-14 text-xs text-[#2c1f20]/40 flex items-center gap-2"><MapPin size={12} /> Milton · Burlington · Virtual across Ontario</p>
        <p className="mt-4 text-[8px] leading-relaxed text-[#2c1f20]/30">Some content on this site was created with the assistance of AI tools and reviewed by Haya Healing Psychotherapy.</p>
      </footer>
    </div>
  );
}
